import {
  CompanyLevelMaterialityResult,
  FinancialMaterialityLevel,
  GenericLevel,
  MaterialityTimeHorizon,
  SeverityLevel,
} from '@upright/common/model/input/materiality-assessment/types';
import { max } from 'lodash';
import { isRelevantMatter } from './matters';
import { isRelevantUprightMatter } from './matters/upright-matters';
import { SustainabilityMatterId } from './types-matters';
import { ResultsOfMatter } from '../../output/materiality/types';
import { CsrdResultTailoring, TailoringType } from './types-tailoring';
import { CompanyLevelMaterialityTriggerType } from './types-company-level-triggers';

export function fmLevelToNumber(level: FinancialMaterialityLevel) {
  switch (level) {
    case FinancialMaterialityLevel.HIGH:
      return 4;
    case FinancialMaterialityLevel.MEDIUM:
      return 3;
    case FinancialMaterialityLevel.LOW:
      return 2;
    case FinancialMaterialityLevel.VERY_LOW:
      return 1;
    case FinancialMaterialityLevel.NONE:
      return 0;
  }
}

function clippedNumberToFmLevel(value: number) {
  switch (value) {
    case 0:
      return FinancialMaterialityLevel.NONE;
    case 1:
      return FinancialMaterialityLevel.VERY_LOW;
    case 2:
      return FinancialMaterialityLevel.LOW;
    case 3:
      return FinancialMaterialityLevel.MEDIUM;
    case 4:
      return FinancialMaterialityLevel.HIGH;
    default:
      return FinancialMaterialityLevel.HIGH;
  }
}

export function numberToFmLevel(value: number): FinancialMaterialityLevel {
  if (value < 0) {
    return FinancialMaterialityLevel.NONE;
  } else if (value > 4) {
    return FinancialMaterialityLevel.HIGH;
  } else {
    return clippedNumberToFmLevel(value);
  }
}

// OH: Used to translate a percentage of geographical operations in risk area to
// a financial materiality level.
export function percentageToFinancialMaterialityLevel(value: number): FinancialMaterialityLevel {
  if (value <= 0.25) {
    return FinancialMaterialityLevel.VERY_LOW;
  } else if (value <= 0.5) {
    return FinancialMaterialityLevel.LOW;
  } else if (value <= 0.75) {
    return FinancialMaterialityLevel.MEDIUM;
  } else {
    return FinancialMaterialityLevel.HIGH;
  }
}

export function magnitudeToFmLevel(value: number) {
  if (value < 0.5) {
    return FinancialMaterialityLevel.NONE;
  } else if (value < 2) {
    return FinancialMaterialityLevel.LOW;
  } else if (value < 4) {
    return FinancialMaterialityLevel.MEDIUM;
  } else {
    return FinancialMaterialityLevel.HIGH;
  }
}

export function scopeToLevel(value: number): GenericLevel {
  if (value <= 0.05) {
    return GenericLevel.VERY_LOW;
  } else if (value <= 0.25) {
    return GenericLevel.LOW;
  } else if (value <= 0.5) {
    return GenericLevel.MEDIUM;
  } else {
    return GenericLevel.HIGH;
  }
}

export function clipNumberToLevel(value: number): GenericLevel {
  if (value < 1.5) {
    return GenericLevel.VERY_LOW;
  } else if (value < 2.5) {
    return GenericLevel.LOW;
  } else if (value < 3.5) {
    return GenericLevel.MEDIUM;
  } else {
    return GenericLevel.HIGH;
  }
}

export function levelToNumber(level: GenericLevel) {
  switch (level) {
    case GenericLevel.VERY_LOW:
      return 1;
    case GenericLevel.LOW:
      return 2;
    case GenericLevel.MEDIUM:
      return 3;
    case GenericLevel.HIGH:
      return 4;
  }
}

export function maxSeverity(severities: SeverityLevel[]): SeverityLevel {
  if (!severities.length) {
    return SeverityLevel.NONE;
  }
  const maximum = max(severities.map(severityLevelToNumber));
  return maximum ? severityToLevel(maximum) : SeverityLevel.NONE;
}

function severityToLevel(value: number) {
  if (value <= 0) {
    return SeverityLevel.NONE;
  } else if (value <= 1) {
    return SeverityLevel.LOW;
  } else if (value <= 2) {
    return SeverityLevel.MEDIUM;
  } else {
    return SeverityLevel.HIGH;
  }
}

export function severityLevelToNumber(level: SeverityLevel) {
  switch (level) {
    case SeverityLevel.NONE:
      return 0;
    case SeverityLevel.LOW:
      return 1;
    case SeverityLevel.MEDIUM:
      return 2;
    case SeverityLevel.HIGH:
      return 3;
  }
}

export function isIrremediableTopic(topic: SustainabilityMatterId) {
  switch (topic) {
    case SustainabilityMatterId.E1_CLIMATE_CHANGE_MITIGATION:
    case SustainabilityMatterId.E2_SUBSTANCES_OF_VERY_HIGH_CONCERN:
    case SustainabilityMatterId.E4_CLIMATE_CHANGE:
    case SustainabilityMatterId.E4_INVASIVE_ALIEN_SPECIES:
    case SustainabilityMatterId.S1_CHILD_LABOUR:
    case SustainabilityMatterId.S1_FORCED_LABOUR:
    case SustainabilityMatterId.S2_CHILD_LABOUR:
    case SustainabilityMatterId.S2_FORCED_LABOUR:
    case SustainabilityMatterId.S4_SECURITY_OF_A_PERSON:
    case SustainabilityMatterId.S4_PROTECTION_OF_CHILDREN:
    case SustainabilityMatterId.G1_ANIMAL_WELFARE:
    case SustainabilityMatterId.UPRIGHT_EQUALITY:
      return true;
    default:
      return false;
  }
}

function hasMaterialResult(results: { imIsMaterial: boolean; fmIsMaterial: boolean }) {
  return results.imIsMaterial || results.fmIsMaterial;
}

export function isRelevantMatterAndMaterial(results: ResultsOfMatter) {
  return isRelevantMatter(results.matterId) && hasMaterialResult(results);
}

export function isRelevantUprightMatterAndMaterial(results: ResultsOfMatter) {
  return isRelevantUprightMatter(results.matterId) && hasMaterialResult(results);
}

export function numberToMaterialityTimeHorizon(value: number) {
  if (value === 1) {
    return MaterialityTimeHorizon.SHORT_TERM;
  } else if (value === 5) {
    return MaterialityTimeHorizon.MEDIUM_TERM;
  } else if (value === 10) {
    return MaterialityTimeHorizon.LONG_TERM;
  }
  return MaterialityTimeHorizon.UNKNOWN;
}

export function severityScoreToSeverityLevel(score: number): SeverityLevel {
  if (score <= 0) {
    return SeverityLevel.NONE;
  } else if (score <= 0.15) {
    return SeverityLevel.LOW;
  } else if (score <= 0.5) {
    return SeverityLevel.MEDIUM;
  } else {
    return SeverityLevel.HIGH;
  }
}

export function severityToCompanyMateriality(severityLevel: SeverityLevel): CompanyLevelMaterialityResult {
  switch (severityLevel) {
    case SeverityLevel.HIGH:
    case SeverityLevel.MEDIUM:
      return CompanyLevelMaterialityResult.MATERIAL;
    case SeverityLevel.NONE:
    case SeverityLevel.LOW:
      return CompanyLevelMaterialityResult.NONE_TRIGGERED;
  }
}

export function toFinancialMaterialityLevel(
  value: number | undefined,
  thresholdsByLevel: { [k in FinancialMaterialityLevel]: number },
): FinancialMaterialityLevel {
  if (!value) {
    return FinancialMaterialityLevel.NONE;
  }
  const orderedMaterialityLevels = [
    FinancialMaterialityLevel.HIGH,
    FinancialMaterialityLevel.MEDIUM,
    FinancialMaterialityLevel.LOW,
  ] as const;
  for (const materialityLevel of orderedMaterialityLevels) {
    if (value >= thresholdsByLevel[materialityLevel]) {
      return materialityLevel;
    }
  }
  return FinancialMaterialityLevel.NONE;
}

export function isAddedOrUpdatedTailoring(tailoring: CsrdResultTailoring): boolean {
  switch (tailoring.type) {
    case TailoringType.PATCH_FINANCIAL:
    case TailoringType.PATCH_IMPACT:
    case TailoringType.PATCH_FINANCIAL_EFFECT:
      return true;
    default:
      return false;
  }
}

export function isDisabledTailoring(tailoring: CsrdResultTailoring): boolean {
  switch (tailoring.type) {
    case TailoringType.REMOVE_FINANCIAL:
    case TailoringType.REMOVE_IMPACT:
      return true;
    default:
      return false;
  }
}

// Takes the description of the trigger and returns whether this should be a geo or a disclosure trigger
// Used for two things:
// 1. To determine the type of the trigger to be created from Excel (where triggers are not separated to geo and disclosure)
// 2. To split custom result parts to disclosure and geo results when reading from the database.
export function getCustomTriggerTypeFromDescription(
  externalDescription: string,
): CompanyLevelMaterialityTriggerType.GEO | CompanyLevelMaterialityTriggerType.DISCLOSURE {
  // Matches "company has operations in", "company has employees in", or "company has suppliers in"
  const geoTriggerRegex = /company has .*(operations|employees|suppliers).* in/i;
  if (geoTriggerRegex.test(externalDescription)) {
    return CompanyLevelMaterialityTriggerType.GEO;
  }

  return CompanyLevelMaterialityTriggerType.DISCLOSURE;
}
